import {
  Attachment,
  DropzoneProps,
  ErrorTypes,
  FileError,
  ValidationResult,
} from "../types";
import { FILE_SIZE_ERROR_NAME, FILE_TYPE_ERROR_NAME } from "../constants";
import { ValidateOptions } from "./types";

/**
 * Функция проверки на скрытый файл в unix-системах
 *
 * Пример: isUnixHiddenFile('.file') -> true
 * Пример: isUnixHiddenFile('file.doc') -> false
 */
export const isUnixHiddenFile = (nameWithExtension: string) => {
  const splittedNameWithExtension = nameWithExtension.split(".");

  return (
    splittedNameWithExtension[0] === "" &&
    splittedNameWithExtension.length === 2
  );
};

/**
 * Функция для получения расширения файла
 *
 * Пример: getFileExtension('file.txt') -> '.txt'
 */
export const getFileExtension = (nameWithExtension: string) => {
  if (isUnixHiddenFile(nameWithExtension) || !nameWithExtension.includes(".")) {
    return "";
  }

  return nameWithExtension.slice(nameWithExtension.lastIndexOf("."));
};

/**
 * Функция для получения имени файла
 *
 * Пример: getFileName('file.txt') -> 'file'
 */
export const getFileName = (nameWithExtension: string) => {
  if (isUnixHiddenFile(nameWithExtension) || !nameWithExtension.includes(".")) {
    return nameWithExtension;
  }

  return nameWithExtension.slice(0, nameWithExtension.lastIndexOf("."));
};

export const getFileId = ({ name, size, lastModified }: File) =>
  size + lastModified + name;

/**
 * Функция указывающая на то, что файл уже был добавлен в список
 */
export const isFileAlreadyAdded = (file: File, attachments: Attachment[]) => {
  const fileId = getFileId(file);

  return attachments.some(({ id }) => fileId === id);
};

/**
 * Функция форматирующая размер файла (входные значения должны быть кратны 1000)
 *
 * Пример: formatFileSize(1000) -> '1 Кб'
 * Пример: formatFileSize(1000000) -> '1 Мб'
 */
export const formatFileSize = (size: number) => {
  const KB = 1000;
  const MB = KB ** 2;

  if (size < KB) {
    return `${size} байт`;
  }

  if (size < MB) {
    return `${(size / KB).toFixed(0)} Кб`;
  }

  return `${(size / MB).toFixed(0)} Мб`;
};

/**
 * Функция форматирующая типы файлов для отображения в удобном для чтения виде
 *
 * Пример: formatFileTypes('.txt,.doc,.js') -> ' txt, doc, js'
 */
export const formatFileTypes = (types: string) => types?.split(".").join(" ");

/**
 * Функция создающая объект ошибки
 */
export const createErrorObject = (
  errorName: ErrorTypes,
  options: ValidateOptions
): FileError => {
  const { acceptedFileTypes, maxFileSize } = options;

  if (errorName === "INVALID_FILE_TYPE" && acceptedFileTypes) {
    return {
      name: errorName,
      message: `Неверный формат файла. Поддерживаются ${formatFileTypes(
        acceptedFileTypes.toString()
      )}`,
    };
  }

  return {
    name: errorName,
    message: `Выберите файл размером до ${formatFileSize(maxFileSize!)}`, // eslint-disable-line @typescript-eslint/no-non-null-assertion
  };
};

/**
 * Функция проверяющая ограничение по размеру файла
 */
export const isValidSize = (
  file: File,
  maxFileSize: DropzoneProps["maxFileSize"]
) => {
  if (maxFileSize === undefined) {
    return true;
  }

  return file.size <= maxFileSize;
};

/**
 * Функция проверяющая ограничение по расширению файлов
 */
export const isValidType = (
  file: File,
  acceptedFileTypes: DropzoneProps["acceptedFileTypes"]
) => {
  if (acceptedFileTypes === undefined || acceptedFileTypes.length === 0) {
    return true;
  }

  const fileExtension = getFileExtension(file.name).toLowerCase();

  return acceptedFileTypes.includes(fileExtension);
};

/**
 * Функция валидирующая файлы на основании переданных опций.
 */
export const validateFiles = (
  files: File[],
  options: ValidateOptions
): ValidationResult[] => {
  const { maxFileSize, acceptedFileTypes } = options;

  return files.map((file) => {
    const validationResult: ValidationResult = { file, errors: [] };

    if (maxFileSize && !isValidSize(file, maxFileSize)) {
      validationResult.errors.push(
        createErrorObject(FILE_SIZE_ERROR_NAME, options)
      );
    }

    if (acceptedFileTypes && !isValidType(file, acceptedFileTypes)) {
      validationResult.errors.push(
        createErrorObject(FILE_TYPE_ERROR_NAME, options)
      );
    }

    return validationResult;
  });
};

/**
 * Форматирует текст в зависимости от переданных ограничений на загрузку файлов
 */
export const createRestrictionText = (
  maxFileSize: DropzoneProps["maxFileSize"],
  acceptedFileTypes: string[]
) => {
  if (!maxFileSize && acceptedFileTypes?.length === 0) {
    return null;
  }

  if (!maxFileSize) {
    return `Форматы — ${formatFileTypes(acceptedFileTypes.toString())}`;
  }

  if (acceptedFileTypes?.length === 0) {
    return `До ${formatFileSize(maxFileSize)}`;
  }

  return `До ${formatFileSize(maxFileSize)}, форматы — ${formatFileTypes(
    acceptedFileTypes.toString()
  )}`;
};

const fileToBase64 = (
  fileObject: File | ValidationResult
): Promise<Attachment> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    let file: File | null = null;

    if (fileObject instanceof File) {
      file = fileObject;
    } else if ("file" in fileObject) {
      file = fileObject.file;
    }

    if (file instanceof File) {
      reader.readAsDataURL(file);

      const attachment: Attachment = {
        id: getFileId(file),
        name: file.name,
        type: file.type,
        fileSource: file,
      };

      reader.onload = () => {
        attachment.blob = reader.result?.toString().split(",")[1];
        if ("errors" in fileObject && !!fileObject?.errors?.length) {
          attachment.errors = fileObject.errors;
        }

        resolve(attachment);
      };

      reader.onerror = (error) => {
        reject(error);
      };
    }
  });

/**
 * Функция преобразующая массив файлов и файлов с ошибкой в массив объектов с типом Attachment и полем blob
 */
export const encodeFilesToAttachments = (
  files: (File | ValidationResult)[]
) => {
  const results = files.map(fileToBase64);

  return Promise.all(results);
};

/**
 * Функция скачивания файла
 */
export const downloadFileFromUI = (file: Attachment) => {
  const fileData = file.fileSource;

  if (!fileData) {
    return;
  }

  const url = window.URL.createObjectURL(fileData);

  const downloadLink = document.createElement("a");

  downloadLink.style.display = "none";
  downloadLink.href = url;
  downloadLink.download = file.name;
  document.body.appendChild(downloadLink);
  downloadLink.click();

  window.URL.revokeObjectURL(url);
  downloadLink.remove();
};

/**
 * Находит вложение (attachment) с заданным id среди переданного списка вложений
 */
export const findAttachmentById = (
  attachments: Attachment[],
  id: string | number
) => attachments.find((file) => file.id === id);

/**
 * Возвращает вложения (attachments) не совпадающие с переданным id
 */
export const filterAttachmentsById = (
  attachments: Attachment[],
  id: string | number
) => attachments.filter((file) => file.id !== id);
